import React from "react";
import { ReactElement, useContext, useEffect, useState, useRef } from "react";
import { StoreContext } from "../../store/Store";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import {
  eventStatus,
  eventType,
  jobStatus,
} from "../../dataProvider/interfaces/IEvent";
import { useAuthenticated, useRedirect, Loading } from "react-admin";
import { routePaths } from "../../baseLayout/SidebarMenu";
import EventModificationTime from "../../components/EventModificationTime/EventModificationTime";

import { MdFileDownload, MdOutlineQrCode } from "react-icons/md";
import { BiChevronDown, BiUndo } from "react-icons/bi";

import moment from "moment-timezone";
import { BiTrash } from "react-icons/bi";
import { TiFolderOpen } from "react-icons/ti";
import { BsInfoCircle } from "react-icons/bs";
import {
  SimpleActionDialog,
  actionDialogType,
  RouteLeavingGuard,
  DropdownButton,
} from "../../components";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { useParams, useNavigate } from "react-router";
import { APIContext } from "../../store/APIStore";
import { PERMISSIONS } from "../../roles";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { theme } from "../../theme";
import { useForm } from "react-hook-form";
import FdButton from "../../components/UIComponents/Button/FdButton";
import { EventFormNew } from "../../components/EventForm/EventFormNew";
import { QrCodeGenerator } from "./QrCodeGenerator";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import IDeveloperAccount from "../../dataProvider/interfaces/IDeveloperAccount";
import { eventInfoStyles as classes } from "./styles";
import { profileNames } from "../../dataProvider/interfaces/IUser";

interface FormData {
  eventName: string;
  eventLocation?: string;
  eventCity?: string;
  eventType?: string;
  eventTimezone?: { label: string; value: string };
  eventStatus?: string;
  eventStartDate?: Date;
  eventEndDate?: Date;
  eventArchiveInterval?: string;
  singleAttendeeSyncUrl?: string;
  autoSwitchSession?: boolean;
  leadRetrieval?: boolean;
  hasFraudDetection?: boolean;
}

type dialogType = {
  open: boolean;
  icon: ReactElement | undefined;
  message: string;
  type: actionDialogType;
  isLoading: boolean;
  actionCallback: Function | undefined;
};

function downloadFile(url: string): void {
  const link = document.createElement("a");
  link.target = "_blank";
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const EventInfo = () => {
  useAuthenticated();
  const redirect = useRedirect();
  // const dataProvider = useDataProvider() as any;
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    message: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
  } as dialogType);
  const [imageEdit, setImageEdit] = useState<File | null>(null);
  const [devInfo, setDevInfo] = useState<IDeveloperAccount | null>(null);
  const [leadData, setLeadData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const notificationRef = useRef<any>();
  const leadRef = useRef<boolean>(false);
  const { eventId } = useParams() as any;
  let navigate = useNavigate();
  let jobChecker: any;
  // const [defaultTime, setDefaultTime] = useState({
  //   startDate: new Date(state.selectedEvent?.startDateTime),
  //   endDate: new Date(state.selectedEvent?.endDateTime),
  // });

  const [popoutQR, setPopoutQR] = useState(false);

  // permissions
  const canManageEvent = useHasPermissions(PERMISSIONS.CAN_MANAGE_EVENT);
  const canDeleteEvent = useHasPermissions(PERMISSIONS.CAN_DELETE_EVENT);
  const canManageLeads = useHasPermissions(PERMISSIONS.CAN_MANAGE_LEADS);

  const initialValues = {
    eventName: state.selectedEvent?.name || "",
    eventCity: state.selectedEvent?.city || "",
    eventLocation: state.selectedEvent?.locationName || "",
    eventTimezone: state.selectedEvent?.timeZone || "",
    eventType: state.selectedEvent?.type || eventType.OFFLINE,
    // eventStartDate: defaultTime.startDate || "",
    // eventEndDate: defaultTime.endDate || "",
    eventStartDate: new Date(state.selectedEvent?.startDateTime) || "",
    eventEndDate: new Date(state.selectedEvent?.endDateTime) || "",
    eventStatus: state.selectedEvent?.status || eventStatus.PRE_EVENT,
    eventArchiveInterval: String(state?.selectedEvent?.archivingPeriod) || "14",
    singleAttendeeSyncUrl: state.selectedEvent?.singleAttendeeSyncUrl,
    autoSwitchSession: state.selectedEvent?.autoSwitchSession || false,
    leadRetrieval: state?.hasLead || false,
    hasFraudDetection: Boolean(state?.selectedEvent?.hasFraudDetection),
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    control,
    reset,
    getValues,

  } = useForm({
    defaultValues: initialValues,
  });

  const saveEnabled = isDirty || imageEdit !== null;

  const fetchData = async () => {
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
    setLoading(false)
  };

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchLeadInfo = async () => {
    try {
      leadRef.current = true;
      const leadEvent = await api.leadsService.getLeadEvent(eventId);
      dispatch({ type: "SET_HAS_LEAD", payload: !!leadEvent.data });
      if (leadEvent.data) {
        setLeadData(leadEvent.data)
      }
      leadRef.current = false;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (eventId && canManageLeads) {
      fetchLeadInfo()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, canManageLeads]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // setDefaultTime((prev) => ({
    //   ...prev,
    //   startDate: new Date(state.selectedEvent?.startDateTime) || "",
    //   endDate: new Date(state.selectedEvent?.endDateTime) || "",
    // }));
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, reset]);



  // const getStatusList = () => {
  //   return (Object.keys(eventStatus) as Array<keyof typeof eventStatus>).map(
  //     (key) => {
  //       return (
  //         <MenuItem key={eventStatus[key]} value={eventStatus[key]}>
  //           {<EventStatus status={eventStatus[key]} />}
  //         </MenuItem>
  //       );
  //     }
  //   );
  // };

  // const getEventTypeList = () => {
  //   return (Object.keys(eventType) as Array<keyof typeof eventType>).map(
  //     (key) => {
  //       return (
  //         <MenuItem key={eventType[key]} value={eventType[key]}>
  //           {eventType[key]}
  //         </MenuItem>
  //       );
  //     }
  //   );
  // };

  const onDeleteClick = () => {
    setConfirmationDialog({
      open: true,
      message: "Are you sure you want to delete your event?",
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      actionCallback: () => {
        // setDirtyForm(false);
        handleDeleteEvent();
      },
    });
  };

  const showResetWarning = () => {
    setConfirmationDialog({
      open: true,
      message: `
        Are you sure you want to delete ALL event check-ins/scans/...?
        This cannot be undone!
      `,
      icon: <TiFolderOpen size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      actionCallback: () => {
        // setDirtyForm(false);
        resetActionCallback();
      },
    });
  };

  // const handleDownloadEvent = async () => {
  //   try {
  //     setConfirmationDialog({
  //       ...confirmationDialog,
  //       open: true,
  //       message: "Generating report. This can take a few minutes.",
  //       isLoading: true,
  //     });

  //     await api.eventService.getEventData(
  //       state.selectedEvent?.id,
  //     );

  //     setConfirmationDialog({
  //       ...confirmationDialog,
  //       open: true,
  //       message: "Report generated!",
  //       isLoading: false,
  //     });
  //   } catch (error: any) {
  //     console.log(error.response);
  //     notificationRef.current?.show(
  //       error.response.data.message,
  //       notificationType.Error
  //     );
  //   }
  // };

  const handleDeleteEvent = async () => {
    try {
      setConfirmationDialog({
        ...confirmationDialog,
        open: true,
        message: "Deleting event. This can take several minutes.",
        isLoading: true,
      });
      const response = (await api.eventService.delete(
        state.selectedEvent?.id
      )) as any;
      if (response?.message === "Event already deleted") {
        setConfirmationDialog({
          ...confirmationDialog,
          open: false,
          message: "",
          isLoading: false,
        });
        notificationRef.current?.show(
          response?.message,
          notificationType.Error
        );
      } else {
        jobChecker = setInterval(() => {
          if (state.selectedEvent) {
            checkJobStatus(state.selectedEvent.id, response?.job);
          }
        }, 3000); // check for the delete job status every 3s
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const checkJobStatus = async (eventId: number, jobId: number) => {
    try {
      const { status, result } = (await api.eventService.getEventJob(
        eventId,
        jobId
      )) as any; // get processing job status
      if (status === jobStatus.FINISHED) {
        clearInterval(jobChecker);
        redirect(`/${routePaths.ACTIVE_EVENTS}`);
      }
      if (status === jobStatus.ERROR) {
        clearInterval(jobChecker);
        notificationRef.current?.show(result, notificationType.Error);
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  // const archiveActionCallback = async () => {
  //   try {
  //     setConfirmationDialog({
  //       ...confirmationDialog,
  //       open: true,
  //       message: state.selectedEvent.archived
  //         ? "Activating event..."
  //         : "Archiving event...",
  //       isLoading: true,
  //     });

  //     const data = await api.eventService.setArchive(
  //       state.selectedEvent?.id,
  //       !state.selectedEvent.archived
  //     );
  //     redirect(
  //       `/${state.selectedEvent.archived
  //         ? routePaths.ACTIVE_EVENTS
  //         : routePaths.ARCHIVED_EVENTS
  //       }`
  //     );
  //   } catch (error: any) {
  //     console.log(error.response);
  //     notificationRef.current?.show(
  //       error.response.data.message,
  //       notificationType.Error
  //     );
  //   }
  // };

  const resetActionCallback = async () => {
    try {
      setConfirmationDialog({
        ...confirmationDialog,
        open: true,
        message: "Resetting event data...",
        isLoading: true,
      });

      await api.eventService.reset(state.selectedEvent?.id);

      setConfirmationDialog({
        ...confirmationDialog,
        open: false,
        isLoading: true,
      });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  // const handleImageSelect = (file: File) => {
  //   setImageEdit(file);
  // };


  const isLeadRetrievalChanged = 'leadRetrieval' in dirtyFields

  const onSubmit = async (values: FormData) => {
    let newEvent = {
      id: state.selectedEvent?.id,
      name: values.eventName,
      archivingPeriod: values.eventArchiveInterval,
      city: values.eventCity,
      locationName: values.eventLocation,
      startDateTime: moment(values.eventStartDate).format("MMMM DD YYYY HH:mm"),
      endDateTime: moment(values.eventEndDate).format("MMMM DD YYYY HH:mm"),
      type: values.eventType,
      status: values.eventStatus,
      timeZone: values.eventTimezone,
      singleAttendeeSyncUrl: values?.singleAttendeeSyncUrl,
      autoSwitchSession: values?.autoSwitchSession,
      hasFraudDetection: values?.hasFraudDetection,
    } as any;

    dispatch({ type: "SET_SELECTED_EVENT", payload: null });
    try {
      setLoading(true)
      if (imageEdit) {
        const imageData = (await api.fileService.uploadImageFile("events", {
          resourceId: state.selectedEvent?.id,
          file: imageEdit,
        })) as any;
        newEvent = { ...newEvent, logoUrl: imageData.image };
        setImageEdit(null);
      }

      const promises: any = [];
      const portalCall = api.eventService.updateEvent(newEvent);
      promises.push(portalCall);

      if (canManageLeads) {
        const leadCall = async () => {
          let apiKey;
          if (values.leadRetrieval) {
            const userPayload = {
              fullName: `Lead Retrieval ${state.selectedEvent?.id}`,
              username: `lr_user_${state.selectedEvent?.id}`,
              password: Math.random().toString(36).slice(2, 10),
              profile: profileNames.VIEWER,
              account: state.selectedEvent?.account?.id,
              email: ''
            }
            userPayload.email = `${userPayload.username}@fielddrive.com`
            const callUserList = await api.accountService.getAccountsUsers(state.selectedEvent?.account?.id) as any;
            const hasUserName = callUserList.find((el: any) => el.username === userPayload.username)
            if (Boolean(hasUserName)) {
              const accounts = await api.developerAccountService.getDeveloperAccount(String(hasUserName?.id)) as any;
              if (accounts && accounts[0]) { apiKey = accounts?.[0]?.apiKey; }
            } else {
              const newUser = await api.userService.create(userPayload) as any;
              const accounts = await api.developerAccountService.getDeveloperAccount(String(newUser?.id)) as any;
              if (accounts && accounts[0]) { apiKey = accounts?.[0]?.apiKey; }
            }
            const leadEventPayload = {
              eventId: state?.selectedEvent?.id?.toString() ?? "",
              name: isLeadRetrievalChanged ? values.eventName : leadData?.name,
              startDate: moment(values.eventStartDate).format('YYYY-MM-DDTHH:mm:ss[Z]'),
              endDate: moment(values.eventEndDate).format('YYYY-MM-DDTHH:mm:ss[Z]'),
              location: values.eventLocation,
              timezone: values.eventTimezone,
              appConfig: {
                apiKey: isLeadRetrievalChanged ? apiKey : leadData?.appConfig?.apiKey,
                archivalDays: values.eventArchiveInterval,
                dailyReportsEnabled: isLeadRetrievalChanged ? (leadData?.appConfig?.dailyReportsEnabled ?? true) : true,
                gdprConfig: {
                  approveButtonLabel: leadData?.appConfig?.gdprConfig.approveButtonLabel || "",
                  cancelButtonLabel: leadData?.appConfig?.gdprConfig.cancelButtonLabel || "",
                  title: leadData?.appConfig?.gdprConfig.title || "",
                  description: leadData?.appConfig?.gdprConfig.description || "",
                  gdprConsent: leadData?.appConfig?.gdprConfig.gdprConsent || false,
                },
              },
            };
            return api.leadsService.enableEvent(leadEventPayload)
          } else {
            api.leadsService.tieLead(state.selectedEvent?.id?.toString() ?? "");
          }
        }
        promises.push(leadCall());
      }

      if (values.leadRetrieval !== undefined) {
        dispatch({ type: "SET_HAS_LEAD", payload: values.leadRetrieval });
      }

      const [event] = await Promise.all(promises);
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
      setLoading(false);
      notificationRef.current?.show(
        "Event successfully updated",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  if (!eventId || eventId === "undefined") {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        paddingTop="50px"
      >
        Please, select an event!
      </Box>
    );
  }

  return (
    <>
      {state.selectedEvent && !leadRef.current && !loading ? (
        <Box sx={classes.wrapper}>
          <Box sx={classes.header}>
            <Box sx={classes.headerLeft}>
              <Box sx={classes.eventLogo} aria-label="eventLogo">
                <FdTypography
                  sx={classes.eventName}
                  label={
                    state.selectedEvent.name &&
                    state.selectedEvent.name.slice(0, 1)
                  }
                />
                {/* <MediaImageUploader
                onImageSelect={handleImageSelect}
                imageUrl={state.selectedEvent.logoUrl}
              /> */}
              </Box>

              <Box>
                <FdTypography label="" variant="h3" component="h3">
                  {state.selectedEvent.name} ({state.selectedEvent.id})
                </FdTypography>

                <Box display="flex" marginTop="5px">
                  {/* {state.selectedEvent.status && (
                  <>
                    <EventStatus status={state.selectedEvent.status} />,
                  </>
                )} */}
                  <Box>
                    <Box component="span" fontWeight="bold">
                      {state.selectedEvent?.registeredAttendeesCount}
                    </Box>{" "}
                    attendees,
                  </Box>
                  <Box marginLeft="5px">
                    <Box component="span" fontWeight="bold">
                      {state.selectedEvent?.arrivedAttendeesCount}
                    </Box>{" "}
                    arrived
                  </Box>
                </Box>
                <Box sx={{ fontSize: "12px" }}>
                  Account: {state.selectedEvent?.account?.name}
                </Box>
              </Box>
            </Box>

            {canManageEvent && (
              <Box textAlign="center">
                <FdButton
                  variant="contained"
                  size="medium"
                  disabled={!saveEnabled}
                  type="submit"
                  sx={classes.saveBtn}
                  className={!saveEnabled ? "notSaved" : "saved"}
                  form="eventForm"
                  ButtonName={saveEnabled ? "Save edits" : "No edits to save"}
                />

                <EventModificationTime
                  dateTime={state.selectedEvent.modifiedOn}
                />
              </Box>
            )}
          </Box>
          <Divider />

          <Box sx={classes.content}>
            <form
              id="eventForm"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              style={{ width: "100%", textAlign: "left" }}
            >
              <EventFormNew
                selectedEvent={state.selectedEvent}
                register={register}
                control={control}
                getValues={getValues}
                errors={errors}
                isEventInfo={true}
                isLead={state.hasLead}
              // reset={reset}
              />
            </form>
            <RouteLeavingGuard
              when={saveEnabled}
              navigate={(path) => navigate(path)}
              shouldBlockNavigation={(location) => saveEnabled}
            />
          </Box>
          <Box mt={2} mx={1}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                {canDeleteEvent && (
                  <Box mr={2} component="span">
                    <FdButton
                      ButtonName="Reset event"
                      variant="text"
                      startIcon={<BiUndo />}
                      onClick={showResetWarning}
                    />
                  </Box>
                )}
                {canDeleteEvent && (
                  <Box mr={1} component="span">
                    <FdButton
                      ButtonName="Delete event"
                      variant="text"
                      startIcon={<BiTrash />}
                      onClick={onDeleteClick}
                    />
                  </Box>
                )}
              </Grid>
              <Grid
                item
                xs={6}
                style={{
                  textAlign: "right",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {/* {canManageEvent && (
                  <Button
                    startIcon={<TiFolderOpen />}
                    onClick={() =>
                      showArchiveWarning(state.selectedEvent.archived)
                    }
                  >
                    {state.selectedEvent.archived ? "Unarchive" : "Archive"}{" "}
                    event
                  </Button>
                )} */}
                {canManageEvent && (
                  <Box mr={2} component="span">
                    {/* @ts-ignore */}
                    <FdButton
                      startIcon={<MdOutlineQrCode />}
                      onClick={() => {
                        setPopoutQR(true);
                      }}
                      ButtonName="Create login code"
                      variant="text"
                      sx={{
                        color: theme.palette.text.primary,
                      }}
                    />
                  </Box>
                )}
                {canManageEvent && (
                  // <Button
                  //     startIcon={<MdFileDownload />}
                  //     href={`${api.apiUrl}/api/v1/report/events/${state.selectedEvent?.id}/excel/complete?token=${token}`}
                  //   >
                  //     Download event report
                  // </Button>
                  <Box
                    component="span"
                    sx={{
                      "& .MuiButton-root": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <DropdownButton
                      label="Download report"
                      component="span"
                      endIcon={<BiChevronDown />}
                      variant="text"
                      startIcon={<MdFileDownload />}
                      options={[
                        {
                          key: "full-report",
                          label: "Full report (can take a while)",
                          callback: () =>
                            downloadFile(
                              api.eventService.getExcelReportUrl(
                                state.selectedEvent?.id
                              )
                            ),
                        },
                        {
                          key: "attendee-list",
                          label: "Attendee list",
                          callback: () =>
                            downloadFile(
                              api.eventService.getAttendeesReportUrl(
                                state.selectedEvent?.id
                              )
                            ),
                        },
                        {
                          key: "check-ins",
                          label: "Check-in logs",
                          callback: () =>
                            downloadFile(
                              api.eventService.getCheckHistoriesReportUrl(
                                state.selectedEvent?.id
                              )
                            ),
                        },
                        {
                          key: "session-scans",
                          label: "Session scan",
                          callback: () =>
                            downloadFile(
                              api.eventService.getSessionScansReportUrl(
                                state.selectedEvent?.id
                              )
                            ),
                        },
                      ]}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
          <Divider />

          <Box sx={classes.footer}>
            <BsInfoCircle
              style={{
                position: "absolute",
                left: 0,
                top: "3px",
                fontSize: "14px",
                color: "#F6A316",
              }}
            />
            <FdTypography
              label="API synchronisation - please note:"
              sx={{ fontWeight: "bold" }}
            />

            <FdTypography
              label="Edits or changes entered in the fielddrive platform do not
              synchronise back to your event registration platform."
            />
            <FdTypography
              label="It is best practice to capture and save all changes in your event
              registration platform. These will then automatically be mirrored
              here in the fielddrive platform. This ensures that all your event
              delegate data remains in one place, and synchronising with our
              platform."
            />
          </Box>
        </Box>
      ) : (
        <Loading />
      )}

      {/* {(!state.selectedEvent || loading) && <Loading />} */}

      <SimpleActionDialog
        type={confirmationDialog.type}
        icon={confirmationDialog.icon}
        open={confirmationDialog.open}
        title={confirmationDialog.message}
        isLoading={confirmationDialog.isLoading}
        onConfirm={confirmationDialog.actionCallback as Function}
        onCancel={handleDialogClose}
        onClose={handleDialogClose}
      />

      <Dialog
        open={popoutQR}
        onClose={() => setPopoutQR(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <DialogTitle id="form-dialog-title">
          Create fdOnsite login code
        </DialogTitle>
        <DialogContent>
          <QrCodeGenerator currentUser={state.currentUser} />
        </DialogContent>
        <DialogActions>
          <FdButton
            onClick={() => {
              const canvas = document.getElementById("react-qrcode-logo");
              const url = (canvas as HTMLCanvasElement)
                .toDataURL("image/png")
                ?.replace("image/png", "image/octet-stream");
              const a = document.createElement("a");
              a.href = url;
              a.download = `${state.selectedEvent.name}_onsite_creds.png`;
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
            sx={{ color: theme.palette.primary.main }}
            variant="text"
            ButtonName="Download"
            aria-label="fd-download"
          />

          <FdButton
            onClick={() => setPopoutQR(false)}
            sx={{ color: theme.palette.primary.main }}
            variant="text"
            ButtonName="Close"
            aria-label="fd-close"
          />
        </DialogActions>
      </Dialog>

      <NotificationMessage ref={notificationRef} />
    </>
  );
};

export default EventInfo;
